import { useState } from "react";
import { CircularProgress, Dialog, DialogTitle } from "@mui/material";
import { ApplicationScreen, Consult } from "../../models";
import { BuildUrl } from "../../helpers";
import { useUserContext } from "../../contexts";
import { useMsal } from "@azure/msal-react";
import { apiAccessRequest } from "../../constants";
import { useCreateAuditLog } from "../../hooks";

interface props {
	consult: Consult;
	open: boolean;
	onClose: (hasBeenRequeued: boolean) => void;
}

export const RequeuePatientModal = ({ consult, open, onClose }: props) => {
	const { account } = useUserContext();
	const { instance } = useMsal();
	const [isError, setIsError] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const { createAuditLog } = useCreateAuditLog();

	const callCreateAuditLog = async () => {
		try {
			await createAuditLog([
				{
					patientId: consult.patientId,
					patientName: `${consult.firstName} ${consult.lastName}`.trim(),
					clinicId: consult.clinicId,
					username: account?.name ?? "",
					applicationScreen: ApplicationScreen.AdminTelehealthQueue,
					action: "Requeued patient"
				}
			]);
		} catch (error) {
			console.log("Error creating audit log", error);
		}
	};

	const handleConfirmClick = async () => {
		setIsError(false);
		setIsLoading(true);
		try {
			const requestUrl = BuildUrl(
				`Consult/${consult.id}/Requeue?recordRequeue=false`
			);

			const tokenResponse = await instance.acquireTokenSilent({
				account,
				scopes: apiAccessRequest.scopes
			});
			const headers = {
				...(tokenResponse && {
					Authorization: `Bearer ${tokenResponse.accessToken}`
				})
			};
			const response = await fetch(requestUrl, {
				method: "POST",
				headers
			});

			if (!response.ok) {
				setIsError(true);
				return;
			}
			onClose(true);
			await callCreateAuditLog();
			// eslint-disable-next-line @typescript-eslint/no-unused-vars
		} catch (error) {
			setIsError(true);
		}

		setIsLoading(false);
	};

	return (
		<Dialog open={open}>
			<DialogTitle
				sx={{
					display: "flex",
					justifyContent: "end",
					fontFamily: "poppins-semibold"
				}}
			>
				<img
					src="/images/svg/close.svg"
					onClick={() => onClose(false)}
					alt="Requeue Patient"
				></img>
			</DialogTitle>

			{!isError && (
				<div
					className="flex flex-col gap-5 p-4 items-center"
					style={{ width: 500 }}
				>
					<img
						src="images/svg/single-neutral-actions-refresh.svg"
						height="72px"
						width="72px"
						alt="Requeue Patient"
					/>

					<div className="ch-m-text" style={{ textAlign: "center" }}>
						{`This is a re-queue for ${consult.firstName} ${consult.lastName}`}
					</div>

					<div className="flex flex-row justify-center mb-4">
						<button
							className="ch-button ch-button-primary me-4"
							onClick={() => onClose(false)}
						>
							Cancel
						</button>
						<button
							className="ch-button ch-button-secondary"
							onClick={() => handleConfirmClick()}
						>
							Confirm Re-queue
						</button>
					</div>
				</div>
			)}
			{isLoading && (
				<div className="flex justify-center py-3">
					<CircularProgress size="80px" />
				</div>
			)}
			{isError && (
				<div className="flex flex-col items-center py-3">
					<img
						src="/images/svg/road-sign-warning.svg"
						height="50"
						alt="error icon"
					/>
					<p className="ch-m-text py-3 text-center">
						An error has occurred. The consult has not been re-queued. Try again
						later
					</p>
					<div className="flex gap-6">
						<button
							className="ch-button ch-button-primary"
							onClick={() => onClose(false)}
						>
							Go back
						</button>
					</div>
				</div>
			)}
		</Dialog>
	);
};
